/**
 * MemberRow Component
 * Display member in horizontal row format with hover-based contextual actions
 */

import { Link } from 'react-router-dom';
import { hasAdminAccess } from '../../lib/roles';
import { UserMinus, RotateCcw } from 'lucide-react';
import type { Member } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useMemberImage } from '../../hooks/useMemberImage';

export interface MemberRowProps {
  member: Member;
  onEdit?: (memberId: number) => void;
  onRemove?: (memberId: number) => void;
  onRestore?: (memberId: number) => void;
  dataUpdatedAt?: number;
}

export const MemberRow = ({ member, onEdit, onRemove, onRestore, dataUpdatedAt }: MemberRowProps) => {
  const { user } = useAuth();

  // Load profile image with automatic retry on blob URL failures
  const { imageUrl: imageBlobUrl, isLoading: imageLoading, handleImageError } = useMemberImage(
    member.id,
    member.has_profile_picture,
    { refreshTrigger: dataUpdatedAt }
  );

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Calculate tenure (years in organization)
  const calculateTenure = (joinDate: string) => {
    const start = new Date(joinDate);
    const now = new Date();
    const years = (now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
    return Math.floor(years);
  };

  const tenure = calculateTenure(member.date_joined);

  return (
    <div className="group relative px-6 py-4 hover:bg-surface-hover active:bg-surface-hover transition-colors">
      {/* Full-row tap target for mobile */}
      <Link
        to={`/members/${member.id}`}
        className="absolute inset-0 z-10 lg:hidden"
        tabIndex={-1}
        aria-label={`View ${member.first_name} ${member.last_name}`}
      />

      <div className="flex items-center justify-between">
        {/* Left: Profile Image */}
        <div className="flex items-center space-x-4 flex-1 min-w-0">
          <Link to={`/members/${member.id}`} className="flex-shrink-0 relative z-20">
            {member.has_profile_picture && imageBlobUrl ? (
              <div className="w-16 h-16 rounded-full bg-surface-secondary flex items-center justify-center overflow-hidden">
                {imageLoading ? (
                  <svg
                    className="animate-spin h-6 w-6 text-blue-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  <img
                    src={imageBlobUrl}
                    alt={`${member.first_name} ${member.last_name}`}
                    className="w-full h-full object-cover"
                    onError={handleImageError}
                  />
                )}
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center text-white text-xl font-bold">
                {member.first_name[0]}
                {member.last_name[0]}
              </div>
            )}
          </Link>

          {/* Center: Member Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <Link
                to={`/members/${member.id}`}
                className="relative z-20 text-lg font-medium text-heading hover:text-blue-600 dark:hover:text-blue-400 truncate"
              >
                {member.first_name} {member.last_name}
              </Link>
              {member.removed_at ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-status-neutral text-status-neutral">
                  Removed
                </span>
              ) : !member.is_active && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-status-danger text-status-danger">
                  Inactive
                </span>
              )}
            </div>
            <div className="mt-1 space-y-1">
              {/* Desktop: Email + Job Title row */}
              <div className="hidden lg:flex items-center space-x-4 text-sm text-meta">
                <span className="truncate">{member.email}</span>
                <span>•</span>
                <span>{member.position || 'No job title'}</span>
              </div>
              {/* Desktop: Team + Date Joined row */}
              <div className="hidden lg:flex items-center space-x-4 text-sm text-meta">
                <span>{member.team_name || (member.team_id ? `Team ${member.team_id}` : 'No Team Assigned')}</span>
                <span>•</span>
                <span>
                  {tenure < 0
                    ? `Joining ${formatDate(member.date_joined)}`
                    : `Joined ${formatDate(member.date_joined)}${tenure > 0 ? ` (${tenure} year${tenure !== 1 ? 's' : ''})` : ''}`
                  }
                </span>
              </div>
              {/* Mobile/Tablet: Job Title only */}
              <div className="lg:hidden text-sm text-meta">
                {member.position || 'No job title'}
              </div>
              {/* Mobile/Tablet: Team only */}
              <div className="lg:hidden text-sm text-meta">
                {member.team_name || (member.team_id ? `Team ${member.team_id}` : 'No Team Assigned')}
              </div>
            </div>
          </div>
        </div>

        {/* Right: Contextual Actions (Hidden by default, shown on hover) */}
        <div className="member-actions-container hidden lg:block">
          <div className="member-actions flex items-center gap-4 opacity-0 translate-x-5 pointer-events-none transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:pointer-events-auto">
            {hasAdminAccess(user?.role) && onEdit && (
              <button
                onClick={() => onEdit(member.id)}
                className="action-btn inline-flex items-center px-3 py-1.5 text-sm font-medium border border-blue-600 rounded-md bg-surface text-heading hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                title="Edit Member"
              >
                <svg
                  className="h-4 w-4 mr-1.5 text-green-600 dark:text-green-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                <span className="hidden sm:inline">Edit</span>
              </button>
            )}
            {hasAdminAccess(user?.role) && onRemove && !member.removed_at && (
              <button
                onClick={() => onRemove(member.id)}
                className="action-btn inline-flex items-center px-3 py-1.5 text-sm font-medium border border-blue-600 rounded-md bg-surface text-heading hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                title="Remove Member"
              >
                <UserMinus className="h-4 w-4 mr-1.5 text-red-600 dark:text-red-400" />
                <span className="hidden sm:inline">Remove</span>
              </button>
            )}
            {hasAdminAccess(user?.role) && onRestore && member.removed_at && (
              <button
                onClick={() => onRestore(member.id)}
                className="action-btn inline-flex items-center px-3 py-1.5 text-sm font-medium border border-blue-600 rounded-md bg-surface text-heading hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                title="Restore Member"
              >
                <RotateCcw className="h-4 w-4 mr-1.5 text-blue-600 dark:text-blue-400" />
                <span className="hidden sm:inline">Restore</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
